<script>
import bem from '@/utils/bem';
import PencilIcon from '@/components/svg/PencilIcon/PencilIcon.vue';
import EditTitleWithLinesModal from '@/components/pageContent/modules/EditTitleWithLinesModal.vue';
import EditDescriptionModal from '@/components/pageContent/modules/EditDescriptionModal.vue';
import EditButtonModal from '@/components/pageContent/modules/EditButtonModal.vue';
import ImageUploadModal from '@/components/pageContent/modules/ImageUploadModal.vue';
import publishGuardMixin from '@/mixins/publishGuardMixin';
import TitleWithLinesBlock from './title-with-lines-block.vue';
import styles from './one-tab-with-image-block-style.scss';

const b = bem('one-tab-with-image-block', styles);

export default {
  mixins: [publishGuardMixin],
  name: 'OneTabWithImageBlock',
  components: {
    PencilIcon,
    EditTitleWithLinesModal,
    EditDescriptionModal,
    EditButtonModal,
    ImageUploadModal,
    TitleWithLinesBlock,
  },
  props: {
    className: {
      type: String,
      default: '',
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    isMobileView: {
      type: Boolean,
      default: false,
    },
    isEditMode: {
      type: Boolean,
      default: false,
    },
    pageKey: {
      type: String,
      default: '',
    },
  },
  emits: ['update'],
  data() {
    return {
      mainTitleModalVisible: false,
      titleModalVisible: false,
      descriptionModalVisible: false,
      buttonModalVisible: false,
      imageModalVisible: false,
    };
  },
  computed: {
    b() {
      return b;
    },
    isMobile() {
      return this.isMobileView;
    },
    // Main title (optional, TitleWithLinesBlock)
    withMainTitle() {
      return this.data.withMainTitle || false;
    },
    mainTitle() {
      return this.data.mainTitle || [];
    },
    hasMainTitle() {
      return this.withMainTitle && this.mainTitle.length > 0;
    },
    // Title (second title or complex)
    withSecondTitle() {
      return this.data.withSecondTitle || false;
    },
    title() {
      return this.data.title || [];
    },
    secondTitle() {
      return this.data.secondTitle || [];
    },
    hasTitle() {
      if (this.withSecondTitle) {
        return Array.isArray(this.secondTitle) && this.secondTitle.length > 0;
      }
      return Array.isArray(this.title) && this.title.length > 0;
    },
    // Description
    description() {
      return this.data.description || '';
    },
    descriptionMobile() {
      return this.data.descriptionMobile || this.description;
    },
    displayDescription() {
      return this.isMobile ? this.descriptionMobile : this.description;
    },
    hasDescription() {
      return !!this.description;
    },
    // Image
    imageSrc() {
      return this.data.imageSrc || '';
    },
    imageSrcMobile() {
      return this.data.imageSrcMobile || this.imageSrc;
    },
    displayImage() {
      return this.isMobile ? this.imageSrcMobile : this.imageSrc;
    },
    // Image text
    imageText() {
      return this.data.imageText || '';
    },
    imageTextMobile() {
      return this.data.imageTextMobile || this.imageText;
    },
    displayImageText() {
      return this.isMobile ? this.imageTextMobile : this.imageText;
    },
    hasImageText() {
      return !!this.imageText;
    },
    // Button
    buttonText() {
      return this.data.buttonText || '';
    },
    buttonTextMobile() {
      return this.data.buttonTextMobile || this.buttonText;
    },
    displayButtonText() {
      return this.isMobile ? this.buttonTextMobile : this.buttonText;
    },
    buttonLink() {
      return this.data.buttonLink || '';
    },
    hasButton() {
      return !!this.buttonText;
    },
    // Options
    isDark() {
      return this.data.isDark || false;
    },
    isDarkButton() {
      return this.data.isDarkButton || false;
    },
    isRight() {
      return this.data.isRight || false;
    },
    withoutShadowImage() {
      return this.data.withoutShadowImage !== false;
    },
    isHomePage() {
      return this.pageKey === 'home';
    },
  },
  methods: {
    // Modal open methods
    openMainTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.mainTitleModalVisible = true;
      }
    },
    openTitleModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.titleModalVisible = true;
      }
    },
    openDescriptionModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.descriptionModalVisible = true;
      }
    },
    openButtonModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.buttonModalVisible = true;
      }
    },
    openImageModal() {
      if (!this.guardPublished()) return;
      if (this.isEditMode) {
        this.imageModalVisible = true;
      }
    },
    // Toggle methods
    togglePosition() {
      if (this.isEditMode) {
        this.$emit('update', { isRight: !this.isRight });
      }
    },
    toggleMainTitle() {
      if (this.isEditMode) {
        this.$emit('update', { withMainTitle: !this.withMainTitle });
      }
    },
    toggleSecondTitle() {
      if (this.isEditMode) {
        this.$emit('update', { withSecondTitle: !this.withSecondTitle });
      }
    },
    // Save handlers
    handleMainTitleSave(values) {
      this.$emit('update', { mainTitle: values });
      this.mainTitleModalVisible = false;
    },
    handleTitleSave(values) {
      if (this.withSecondTitle) {
        this.$emit('update', { secondTitle: values });
      } else {
        this.$emit('update', { title: values });
      }
      this.titleModalVisible = false;
    },
    handleDescriptionSave(values) {
      this.$emit('update', {
        description: values.description,
        descriptionMobile: values.descriptionMobile,
      });
      this.descriptionModalVisible = false;
    },
    handleButtonSave(values) {
      this.$emit('update', {
        buttonText: values.buttonText,
        buttonTextMobile: values.buttonTextMobile,
        buttonLink: values.buttonLink,
      });
      this.buttonModalVisible = false;
    },
    handleButtonDelete() {
      this.$emit('update', {
        buttonText: '',
        buttonTextMobile: '',
        buttonLink: '',
      });
      this.buttonModalVisible = false;
    },
    handleMainTitleDelete() {
      this.$emit('update', { mainTitle: [] });
      this.mainTitleModalVisible = false;
    },
    handleTitleDelete() {
      if (this.withSecondTitle) {
        this.$emit('update', { secondTitle: [] });
      } else {
        this.$emit('update', { title: [] });
      }
      this.titleModalVisible = false;
    },
    handleDescriptionDelete() {
      this.$emit('update', { description: '', descriptionMobile: '' });
      this.descriptionModalVisible = false;
    },
    handleImageDelete() {
      this.$emit('update', {
        imageSrc: '',
        imageSrcMobile: '',
        imageText: '',
        imageTextMobile: '',
      });
      this.imageModalVisible = false;
    },
    handleImageSave(values) {
      this.$emit('update', {
        imageSrc: values.image || this.imageSrc,
        imageSrcMobile: values.imageMobile || this.imageSrcMobile,
        imageText: values.imageText ?? this.imageText,
        imageTextMobile: values.imageTextMobile ?? this.imageTextMobile,
      });
      this.imageModalVisible = false;
    },
  },
};
</script>

<template>
  <article :class="b('', { mix: className, isMobileView, isEditMode, isDark })">
    <!-- Edit controls (edit mode only) -->
    <div v-if="isEditMode && !isMobileView" :class="b('toggle-controls')">
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label')">Info Block Position:</span>
        <a-button
          size="small"
          :type="!isRight ? 'primary' : 'default'"
          @click="togglePosition"
        >
          Left
        </a-button>
        <a-button
          size="small"
          :type="isRight ? 'primary' : 'default'"
          @click="togglePosition"
        >
          Right
        </a-button>
      </div>
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label')">Main Title:</span>
        <a-switch
          :checked="withMainTitle"
          @change="toggleMainTitle"
        />
      </div>
      <div :class="b('toggle-item')">
        <span :class="b('toggle-label')">Second Title:</span>
        <a-switch
          :checked="withSecondTitle"
          @change="toggleSecondTitle"
        />
      </div>
    </div>

    <!-- Main Title (optional, above content) -->
    <div
      v-if="hasMainTitle || (isEditMode && withMainTitle)"
      :class="b('main-title-wrap', { isMobileView, editable: isEditMode })"
      :role="isEditMode ? 'button' : undefined"
      :tabindex="isEditMode ? 0 : undefined"
      @click="openMainTitleModal"
      @keyup.enter="openMainTitleModal"
      @keyup.space="openMainTitleModal"
    >
      <TitleWithLinesBlock
        v-if="mainTitle.length"
        :class="b('main-title', { isMobileView })"
        :is-mobile="isMobile"
        :is-mobile-view="isMobileView"
        :text-list="mainTitle"
      />
      <div v-else-if="isEditMode" :class="b('add-field')">
        + Add main title
      </div>
      <PencilIcon
        v-if="isEditMode && mainTitle.length"
        :class="b('edit-icon')"
        :color="isDark ? '#ffffff' : '#293241'"
        :size="20"
      />
    </div>

    <!-- Content wrapper -->
    <div :class="b('content-wrap', { isRight, isMobileView })">
      <!-- Image block -->
      <div
        v-if="!isMobileView"
        :class="b('image-block', { isMobileView })"
      >
        <div
          :class="b('image-placeholder', {
            editable: isEditMode,
            withoutShadow: withoutShadowImage,
            withShadow: !withoutShadowImage,
            isMobileView,
          })"
          :style="displayImage ? {
            backgroundImage: `url(${displayImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openImageModal"
          @keyup.enter="openImageModal"
          @keyup.space="openImageModal"
        >
          <span v-if="!displayImage" :class="b('placeholder-text')">
            <span :class="b('placeholder-plus')">+</span>
            <br>CLICK TO UPLOAD
          </span>
        </div>

        <!-- Image text (optional, shown if set in image modal) -->
        <p v-if="hasImageText" :class="b('image-text', { isMobileView })">
          {{ displayImageText }}
        </p>
      </div>

      <!-- Info block -->
      <div :class="b('info-block', { isRight, isMobileView })">
        <!-- Title -->
        <div
          :class="b('title-wrap', { editable: isEditMode })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openTitleModal"
          @keyup.enter="openTitleModal"
          @keyup.space="openTitleModal"
        >
          <!-- Second title (TitleWithLinesBlock) -->
          <template v-if="withSecondTitle">
            <TitleWithLinesBlock
              v-if="hasTitle"
              :class="b('second-title', { isMobileView, homePage: isHomePage })"
              :is-mobile="isMobile"
              :is-mobile-view="isMobileView"
              :text-list="secondTitle"
            />
            <div v-else-if="isEditMode" :class="b('add-field')">
              + Add title
            </div>
          </template>
          <!-- Complex title (TitleWithLinesBlock) -->
          <template v-else>
            <TitleWithLinesBlock
              v-if="hasTitle"
              :class="b('title', { isMobileView })"
              :is-mobile="isMobile"
              :is-mobile-view="isMobileView"
              :text-list="title"
            />
            <div v-else-if="isEditMode" :class="b('add-field')">
              + Add title
            </div>
          </template>
          <PencilIcon
            v-if="isEditMode && hasTitle"
            :class="b('edit-icon')"
            :color="isDark ? '#ffffff' : '#293241'"
            :size="20"
          />
        </div>

        <!-- Mobile mage block -->
        <div
          v-if="isMobileView"
          :class="b('image-block', { isMobileView: true })"
        >
          <div
            :class="b('image-placeholder', {
            editable: isEditMode,
            withoutShadow: withoutShadowImage,
            withShadow: !withoutShadowImage,
            isMobileView,
          })"
            :style="displayImage ? {
            backgroundImage: `url(${displayImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          } : {}"
            :role="isEditMode ? 'button' : undefined"
            :tabindex="isEditMode ? 0 : undefined"
            @click="openImageModal"
            @keyup.enter="openImageModal"
            @keyup.space="openImageModal"
          >
          <span v-if="!displayImage" :class="b('placeholder-text')">
            <span :class="b('placeholder-plus')">+</span>
            <br>CLICK TO UPLOAD
          </span>
          </div>

          <!-- Image text (optional, shown if set in image modal) -->
          <p v-if="hasImageText" :class="b('image-text', { isMobileView })">
            {{ displayImageText }}
          </p>
        </div>

        <!-- Description (optional) -->
        <div
          v-if="hasDescription || isEditMode"
          :class="b('description-wrap', { editable: isEditMode })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openDescriptionModal"
          @keyup.enter="openDescriptionModal"
          @keyup.space="openDescriptionModal"
        >
          <p
            v-if="hasDescription"
            :class="b('description', { isWhite: isDark, isMobileView })"
          >
            {{ displayDescription }}
          </p>
          <div v-else-if="isEditMode" :class="b('add-field')">
            + Add description
          </div>
          <PencilIcon
            v-if="isEditMode && hasDescription"
            :class="b('edit-icon')"
            :color="isDark ? '#ffffff' : '#293241'"
            :size="18"
          />
        </div>

        <!-- Button (optional) -->
        <div
          v-if="hasButton || isEditMode"
          :class="b('button-wrap', { isMobileView, editable: isEditMode, homePage: isHomePage })"
          :role="isEditMode ? 'button' : undefined"
          :tabindex="isEditMode ? 0 : undefined"
          @click="openButtonModal"
          @keyup.enter="openButtonModal"
          @keyup.space="openButtonModal"
        >
          <div
            v-if="hasButton"
            :class="b('button', { dark: isDarkButton, isMobileView, homePage: isHomePage })"
          >
            {{ displayButtonText }}
            <PencilIcon
              v-if="isEditMode"
              :class="b('edit-icon', { inline: true })"
              :color="isDark || isHomePage ? '#ffffff' : '#293241'"
              :size="16"
            />
          </div>
          <div v-else-if="isEditMode" :class="b('add-field')">
            + Add button
          </div>
        </div>
      </div>
    </div>

    <!-- Modals -->
    <!-- Main Title Modal (TitleWithLinesBlock) -->
    <EditTitleWithLinesModal
      :visible="mainTitleModalVisible"
      :data="mainTitle"
      :show-delete="mainTitle.length > 0"
      @save="handleMainTitleSave"
      @cancel="mainTitleModalVisible = false"
      @delete="handleMainTitleDelete"
    />

    <!-- Title Modal -->
    <EditTitleWithLinesModal
      v-if="withSecondTitle"
      :visible="titleModalVisible"
      :data="secondTitle"
      :show-delete="secondTitle.length > 0"
      @save="handleTitleSave"
      @cancel="titleModalVisible = false"
      @delete="handleTitleDelete"
    />
    <EditTitleWithLinesModal
      v-else
      :visible="titleModalVisible"
      :data="title"
      :show-delete="title.length > 0"
      @save="handleTitleSave"
      @cancel="titleModalVisible = false"
      @delete="handleTitleDelete"
    />

    <!-- Description Modal -->
    <EditDescriptionModal
      :visible="descriptionModalVisible"
      :description="description"
      :description-mobile="descriptionMobile"
      @close="descriptionModalVisible = false"
      @save="handleDescriptionSave"
      @delete="handleDescriptionDelete"
    />

    <!-- Button Modal -->
    <EditButtonModal
      :visible="buttonModalVisible"
      :button-text="buttonText"
      :button-text-mobile="buttonTextMobile"
      :button-link="buttonLink"
      @close="buttonModalVisible = false"
      @save="handleButtonSave"
      @delete="handleButtonDelete"
    />

    <!-- Image Upload Modal -->
    <ImageUploadModal
      :visible="imageModalVisible"
      :image="imageSrc"
      :image-mobile="imageSrcMobile"
      :image-text="imageText"
      :image-text-mobile="imageTextMobile"
      :with-image-text="true"
      :show-delete="!!imageSrc"
      @close="imageModalVisible = false"
      @save="handleImageSave"
      @delete="handleImageDelete"
    />
  </article>
</template>
